/**
 * A Discord bot league's trades on the site: the list (approved ones for everyone; pending and rejected ones for
 * the players in them and the staff), a player's proposal form, and Approve or Reject for staff. The bot's own
 * rules apply: the limit counts Pokémon received, approved trades change rosters when the next week is posted.
 */
import { useState } from 'preact/hooks';
import { api, type BotLeague, type BotPlayer, type BotPrices } from '../api';
import { ActionButton, toast } from '../ui';

type Trade = BotLeague['trades'][number];

function ProposeTrade({ league, me, pool, prices, reload }: {
	league: BotLeague, me: BotPlayer, pool: BotLeague['pools'][number], prices: BotPrices | null, reload: () => void,
}) {
	const [partnerId, setPartnerId] = useState(0);
	const [drops, setDrops] = useState<string[]>([]);
	const [gets, setGets] = useState<string[]>([]);
	const [adding, setAdding] = useState('');
	const partner = pool.players.find(p => p.id === partnerId);
	const toggle = (list: string[], set: (next: string[]) => void, name: string) =>
		set(list.includes(name) ? list.filter(n => n !== name) : [...list, name]);

	return <div class="card">
		<h3 style="margin-top:0">Propose a trade</h3>
		<div class="row">
			<span class="small">With</span>
			<select value={partnerId} onChange={e => {
				setPartnerId(Number((e.target as HTMLSelectElement).value));
				setGets([]);
			}}>
				<option value={0}>Free agency (the undrafted pool)</option>
				{pool.players.filter(p => p.id !== me.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
			</select>
		</div>
		<div class="grid" style="margin-top:8px">
			<div>
				<h4 style="margin:4px 0">You give up</h4>
				{me.roster.map(mon => <label key={mon.pokemon} class="row small"><input type="checkbox" checked={drops.includes(mon.pokemon)}
					onChange={() => toggle(drops, setDrops, mon.pokemon)} /> {mon.pokemon} <span class="muted">({mon.cost})</span></label>)}
				{!drops.length && <p class="small muted">Nothing: pay with the {me.budgetRemaining} points you have left.</p>}
			</div>
			<div>
				<h4 style="margin:4px 0">You receive</h4>
				{partner ? partner.roster.map(mon => <label key={mon.pokemon} class="row small"><input type="checkbox" checked={gets.includes(mon.pokemon)}
					onChange={() => toggle(gets, setGets, mon.pokemon)} /> {mon.pokemon} <span class="muted">({mon.cost})</span></label>) : <>
					{gets.map(name => <div key={name} class="row small">{name}
						<button type="button" class="small" onClick={() => setGets(gets.filter(n => n !== name))}>✕</button></div>)}
					<form class="row" onSubmit={e => {
						e.preventDefault();
						if (adding.trim() && !gets.includes(adding.trim())) setGets([...gets, adding.trim()]);
						setAdding('');
					}}>
						<input list="free-agents" placeholder="An undrafted Pokémon" value={adding} onInput={e => setAdding((e.target as HTMLInputElement).value)} />
						<datalist id="free-agents">{prices?.entries.map(e => <option key={e.name} value={e.name}>{e.cost} pts</option>)}</datalist>
						<button type="submit" class="small">Add</button>
					</form>
				</>}
			</div>
		</div>
		<div class="row" style="margin-top:10px">
			<ActionButton class="primary" disabled={!gets.length} action={async () => {
				try {
					const result = await api.post<{ trade_number: number, free: boolean, warnings: string[] }>(`/api/bot-leagues/${league.id}/trades`, {
						drops, gets, partnerId: partnerId || null,
					});
					toast(`Trade #${result.trade_number} is waiting for the staff.${result.free ? ` It's in the free window, so it doesn't count.` : ''}` +
						`${result.warnings.length ? ` Worth checking: ${result.warnings.join(' ')}` : ''}`, 'good');
					setDrops([]);
					setGets([]);
					reload();
				} catch (error) {
					toast((error as Error).message, 'bad');
				}
			}}>Propose it</ActionButton>
			<span class="small muted">Staff approve it; then rosters change when the next week is posted.</span>
		</div>
	</div>;
}

export function BotTrades({ league, staff, prices, reload }: {
	league: BotLeague, staff: boolean, prices: BotPrices | null, reload: () => void,
}) {
	const nameOf = (id: number | null) => league.pools.flatMap(p => p.players).find(p => p.id === id)?.name || 'Someone';
	const pool = league.pools.find(p => p.you !== null);
	const me = pool?.players.find(p => p.id === pool.you);
	const canPropose = !!me && league.website && league.mode === 'full_season' && !['archived', 'cancelled'].includes(league.status);
	const decide = (trade: Trade, decision: 'approve' | 'reject') => async () => {
		try {
			const result = await api.post<{ week?: number }>(`/api/bot-leagues/${league.id}/trades/${trade.number}/${decision}`);
			toast(decision === 'approve' ? `Trade #${trade.number} approved: it takes effect from week ${result.week}.` : `Trade #${trade.number} rejected.`, 'good');
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	};
	const status = (t: Trade) => t.status === 'pending' ? <span class="badge warn">waiting for staff</span> :
		t.status === 'rejected' ? <span class="badge bad">rejected</span> :
		t.inEffect ? t.week && <span class="badge">from week {t.week}</span> : <span class="badge warn">approved, from week {league.currentWeek + 1}</span>;

	return <>
		<p class="muted small">
			{league.maxTrades > 0 ? `Each player may pick up ${league.maxTrades} Pokémon in trades this season (what they receive counts, not what they drop). ` : ''}
			Approved trades change rosters when the next week is posted.
		</p>
		{canPropose && <ProposeTrade league={league} me={me!} pool={pool!} prices={prices} reload={reload} />}
		{league.trades.length ? <div class="card"><ul class="history">{[...league.trades].reverse().map(t => <li key={t.number}>
			<span class="muted small">#{t.number} </span><strong>{nameOf(t.proposer)}</strong> gives up {t.drops.length ? t.drops.join(', ') : 'nothing'}
			{t.partner !== null ? <> to <strong>{nameOf(t.partner)}</strong></> : ' (free agency)'} for {t.gets.join(', ')}{' '}
			{status(t)}
			{t.free && <span class="badge good">free window</span>}
			{t.at && <span class="muted small"> · {new Date(t.at).toLocaleDateString()}</span>}
			{staff && t.status === 'pending' && <span class="row" style="display:inline-flex;margin-left:8px">
				<ActionButton class="small primary" action={decide(t, 'approve')}>Approve</ActionButton>
				<ActionButton class="small" confirm={`Reject trade #${t.number}?`} action={decide(t, 'reject')}>Reject</ActionButton>
			</span>}
		</li>)}</ul></div> : <p class="empty">No trades yet.</p>}
	</>;
}
